// Libs
import classNames from 'classnames/bind';
import { Grid2, IconButton, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { BaseInput, BaseSelect } from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  REG_EXP,
} from '~/utils/constants/common';
import { formatCurrency, formatInputDecimal } from '~/utils/helper/common';
import { IQueryParamService, ISelectService } from '~/utils/interface/service';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getServiceList } from '~/thunks/service/serviceThunk';
import useDebounce from '~/utils/hooks/useDebounce';
import { IFormEstimate } from '~/utils/interface/estimate';
// Styles, images, icons
import styles from './EstimateService.module.scss';
import { icons } from '~/assets';

type Props = {
  index: number;
  onRemoveItem: (index: number) => void;
};

const cx = classNames.bind(styles);

const EstimateService = (props: Props) => {
  //#region Destructuring Props
  const { index, onRemoveItem } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate']);

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<IFormEstimate>();
  const dispatch = useAppDispatch();

  const price = useWatch({ control, name: `projectedServices.${index}.price` });
  const quantity = useWatch({ control, name: `projectedServices.${index}.quantity` });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [optionService, setOptionService] = useState<ISelectService[]>([]);
  const [serviceSearchKey, setServiceSearchKey] = useState<string>(EMPTY_STRING);
  const debounceServiceSearchKey = useDebounce(serviceSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoadingService, setIsLoadingService] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const total = useMemo(() => {
    return String(Number(price) * Number(quantity));
  }, [price, quantity]);
  //#endregion Declare Memo

  useEffect(() => {
    setValue(`projectedServices.${index}.totalPrice`, total, {
      shouldDirty: true,
    });
  }, [total, index, setValue]);

  const handleGetServiceList = useCallback(
    (params: IQueryParamService) => {
      setIsLoadingService(true);

      dispatch(getServiceList(params))
        .unwrap()
        .then((res) => {
          const { responses } = res?.data;

          if (!responses) return;

          const dataService: ISelectService[] = responses.map((item) => ({
            value: item?.id,
            label: item?.name || EMPTY_STRING,
            price: item?.price || EMPTY_STRING,
            cost: item?.cost || EMPTY_STRING,
            description: item?.description || EMPTY_STRING,
          }));
          setOptionService(dataService);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoadingService(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamService = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceServiceSearchKey && { textSearch: debounceServiceSearchKey }),
    };

    handleGetServiceList(params);
  }, [debounceServiceSearchKey, handleGetServiceList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleOnchangeService = (value: ISelectService) => {
    setValue(`projectedServices.${index}.serviceId`, value?.value || EMPTY_STRING, { shouldDirty: true });
    setValue(`projectedServices.${index}.price`, value?.price || EMPTY_STRING, { shouldDirty: true });
    setValue(`projectedServices.${index}.cost`, value?.cost || EMPTY_STRING, { shouldDirty: true });
    setValue(`projectedServices.${index}.description`, value?.description || EMPTY_STRING, {
      shouldDirty: true,
    });
  };

  const handleSearchServiceChange = (searchKey: string) => {
    setServiceSearchKey(searchKey);
  };
  //#endregion Handle Function

  return (
    <Grid2
      component='div'
      id='estimateServicesComponent'
      className={cx('container')}
      container
      rowSpacing={'12px'}
      columnSpacing={'12px'}
    >
      <Grid2
        size={'grow'}
        container
        rowSpacing={'12px'}
        columnSpacing={'12px'}
        className={cx(index !== DEFAULT_NUMBER_ZERO ? 'item' : EMPTY_STRING)}
      >
        <Grid2 container size={12}>
          <Grid2 size={'grow'}>
            <Controller
              name={`projectedServices.${index}.serviceId`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseSelect
                  label={t('field_service_name_label')}
                  value={value || EMPTY_STRING}
                  onChange={(optionValue) => handleOnchangeService(optionValue)}
                  options={optionService || []}
                  errorMessage={errors?.projectedServices?.[index]?.serviceId?.message}
                  required
                  onSearchChange={handleSearchServiceChange}
                  isLoading={isLoadingService}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`projectedServices.${index}.quantity`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_service_quantity_label')}
                  value={value}
                  required
                  onChange={(event) => {
                    const newValue = formatInputDecimal(event.target.value);
                    setValue(`projectedServices.${index}.quantity`, newValue, { shouldValidate: true });
                  }}
                  errorMessage={errors?.projectedServices?.[index]?.quantity?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`projectedServices.${index}.price`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_service_price_label')}
                  value={value}
                  required
                  onChange={(event) => {
                    const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                    const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;

                    setValue(`projectedServices.${index}.price`, number, { shouldValidate: true });
                  }}
                  errorMessage={errors?.projectedServices?.[index]?.price?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`projectedServices.${index}.cost`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_service_cost_label')}
                  value={value}
                  required
                  onChange={(event) => {
                    const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                    const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;

                    setValue(`projectedServices.${index}.cost`, number, { shouldValidate: true });
                  }}
                  errorMessage={errors?.projectedServices?.[index]?.cost?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 className={cx('totalField')} width={'auto'}>
            <Typography className={cx('textTotal')}>{formatCurrency(total)}</Typography>
            <Grid2 size='auto' className='flex justify-center items-center'>
              <IconButton className={cx('closeField')} onClick={() => onRemoveItem(index)}>
                <img src={icons.commonIconClose} alt={t('common_alt_img', { ns: 'common/base' })} />
              </IconButton>
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 container size={12}>
          <Grid2 size={12}>
            <Controller
              name={`projectedServices.${index}.description`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_service_description_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.projectedServices?.[index]?.description?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default memo(EstimateService);
